import { AxiosResponse } from 'axios'
import { API } from '../config'
import {
  AnimalHistoricDTO,
  AnimalImageDto,
  AnimalRecordDto,
  BloodGradeDto,
  CreateVaccineHistoryDto,
  IAnimal,
  ProductiveAnimalDTO,
  UpdateAnimalDto,
  UpdateAnimalImageDto,
  VaccineHistoryAnimalDto
} from 'src/dto/animal.dto'
import { AnimalHistoricParams } from 'src/domain/animal/AnimalHistoric'
import { AnimalProductive } from 'src/domain/animal/ProductiveAnimal'
import { Animal } from 'src/domain/animal/Animal'
import { AnimalMovementService } from './animal-movement.service'
import { EarringDto } from 'src/dto/birth-certificate.dto'
import { BirthCertificateController } from 'src/controllers/birth-certificate.controller'

const BASE_URL = '/entity/Animal'

/**
 * Serviço centralizado para gerenciamento de animais.
 * Segue arquitetura em camadas, orquestrando:
 * - Criação/atualização de animais
 * - Criação de registros produtivos
 * - Criação de registros históricos
 */
export class AnimalService {
  public async getAnimalListByFarmId(farmId: string, params: any): Promise<AxiosResponse<IAnimal[]>> {
    return await API.get<IAnimal[]>(`${BASE_URL}/${farmId}`, { params })
  }

  public async getAnimalById(farmId: string, id: string): Promise<AxiosResponse<IAnimal>> {
    return await API.get<IAnimal>(`${BASE_URL}/${farmId}/${id}`)
  }

  public async updateAnimal(farmId: string, updateAnimalDto: UpdateAnimalDto): Promise<AxiosResponse<IAnimal>> {
    return await API.put(`${BASE_URL}/${farmId}`, updateAnimalDto)
  }

  public async getAnimalListByLoteId(farmId: string, loteId: string, params: any): Promise<AxiosResponse<IAnimal[]>> {
    return await API.get(`${BASE_URL}/${farmId}/${loteId}/Lote`, { params })
  }

  public async getAnimalImageData(farmId: string, idAnimal: string): Promise<AxiosResponse<AnimalImageDto>> {
    return await API.get(`/entity/imagem/AnimalImagem/${farmId}/${idAnimal}`)
  }

  public async getAnimalImage(farmId: string, idAnimal: string): Promise<AxiosResponse<Blob>> {
    return await API.get(`/image/${farmId}/${idAnimal}`, { responseType: 'blob' })
  }

  public async updateAnimalImage(updateAnimalImageDto: UpdateAnimalImageDto): Promise<AxiosResponse<any>> {
    const { farmId, animalId, imageBase64 } = updateAnimalImageDto
    return await API.put(`/entity/AnimalImagem/${farmId}`, {
      idFazenda: farmId,
      idAnimal: animalId,
      imagem: imageBase64
    })
  }

  public async getVaccineHistoryByAnimal(
    farmId: string,
    animalId: string
  ): Promise<AxiosResponse<VaccineHistoryAnimalDto[]>> {
    return await API.get(`/entity/AnimalHistoricoVacinas/${farmId}/${animalId}/AnimalHistoricoVacinas`)
  }

  public async getBloodGradeList(): Promise<AxiosResponse<BloodGradeDto[]>> {
    return await API.get(`/entity/Sangue`)
  }

  public async getAnimalRecordsList(farmId: string, animalId: string): Promise<AxiosResponse<AnimalRecordDto[]>> {
    return await API.get(`/entity/AnimalMovimentacao/${farmId}/${animalId}`)
  }

  public async getTotalPagesAnimalList(
    farmId: string,
    params?: any
  ): Promise<AxiosResponse<{ Total: number; totalPages: number }>> {
    return await API.get(`/entity/countPages/Animal/${farmId}`, { params })
  }

  public async getProductiveAnimalList(
    farmId: string,
    animalId: string
  ): Promise<AxiosResponse<ProductiveAnimalDTO[]>> {
    return await API.get(`/entity/AnimalProdutivo/${farmId}/${animalId}`)
  }

  public async createProductiveAnimal(farmId: string, data?: any): Promise<AxiosResponse<ProductiveAnimalDTO>> {
    return await API.put(`/entity/AnimalProdutivo/${farmId}`, data)
  }

  public async createAnimalVaccineHistoric(
    farmId: string,
    data?: CreateVaccineHistoryDto
  ): Promise<AxiosResponse<any>> {
    return await API.put(`/entity/AnimalHistoricoVacinas/${farmId}`, data)
  }

  public async createAnimalHistoric(
    farmId: string,
    data?: AnimalHistoricParams
  ): Promise<AxiosResponse<AnimalHistoricDTO>> {
    return await API.put(`/entity/AnimalHistorico/${farmId}`, data)
  }

  // ========== Métodos de Orquestração (Operações Compostas) ==========

  /**
   * Cria um animal completo com todos os registros subjacentes
   * Este método orquestra:
   * 1. Criação do animal principal
   * 2. Criação do registro produtivo (AnimalProdutivo)
   * 3. Criação do registro histórico inicial (AnimalHistorico)
   * 4. Criação do registro de movimentação de nascimento (AnimalMovimentacao)
   * 5. Atualização do status do brinco para "Usado" caso um brinco tenha sido carregado da ficha de nascimento
   *
   * @param farmId - ID da fazenda
   * @param animalData - Dados do animal
   * @returns Animal criado com registros produtivo e histórico
   */
  public async createAnimalComplete(
    farmId: string,
    username: string,
    animalDataPayload: UpdateAnimalDto
  ): Promise<{
    animal: IAnimal
    productive: ProductiveAnimalDTO
    historic: AnimalHistoricDTO
    movimentation?: any
  }> {
    const { earringSelected, ...animalData } = animalDataPayload

    try {
      // 1. Criar o animal principal
      const animalFormated = new Animal(animalData)
      const animalResponse = await this.updateAnimal(farmId, animalFormated)
      const createdAnimal = animalResponse.data

      // 2. Criar registro produtivo
      const productiveData = new AnimalProductive({
        idFazenda: farmId,
        idAnimal: createdAnimal.idAnimal!,
        peso: animalData.pesoAtual || 0,
        nome: animalData.nome || '',
        idLote: animalData.idLoteAtual,
        idadeDias: animalData.dataNascimento
          ? Math.floor((Date.now() - new Date(animalData.dataNascimento).getTime()) / (1000 * 60 * 60 * 24))
          : 0
      })

      const productiveResponse = await this.createProductiveAnimal(farmId, productiveData)

      // 3. Criar registro histórico inicial
      const historicData: AnimalHistoricParams = {
        idFazenda: farmId,
        idAnimal: createdAnimal.idAnimal!,
        peso: animalData.pesoAtual || 0,
        observacoes: animalData.observacoes || '',
        nascimento: true
      }

      const historicResponse = await this.createAnimalHistoric(farmId, historicData)

      // 4. Criar registro de movimentação de nascimento
      const newMovimentation = await new AnimalMovementService().createBirthMovement({
        farmId,
        animalName: animalData.nome!,
        animalId: createdAnimal.idAnimal!,
        username,
        loteDestino: animalData.idLoteAtual,
        pastoDestino: animalData.idPastoAtual
      })

      // 5. Atualizar o status do brinco para "Usado" caso um brinco tenha sido carregado da ficha de nascimento
      if (earringSelected) {
        if (earringSelected.numeroCompleto === animalData.brinco && earringSelected.numeroSufixo === animalData.cdn) {
          const birthCertificateController = new BirthCertificateController()
          await birthCertificateController.updateEarring(farmId, {
            ...earringSelected,
            data: earringSelected.data ? earringSelected.data : new Date().toISOString(),
            status: 'usado'
          })
        }
      }

      return {
        animal: createdAnimal,
        productive: productiveResponse.data,
        historic: historicResponse.data,
        movimentation: newMovimentation
      }
    } catch (error) {
      console.error('Erro ao criar animal completo:', error)
      throw error
    }
  }
}
